// dataService.js
import { supabase } from '../supabaseClient';
import { getAPIUrl } from '../config';
import { buildSelectedModifierPayload, normalizeModifierSummary } from './itemUtils';

const API_URL = getAPIUrl();

const parseModifierSnapshot = (rawValue) => {
  if (!rawValue) {
    return [];
  }

  try {
    const parsed = typeof rawValue === 'string' ? JSON.parse(rawValue) : rawValue;
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.map((entry) => ({
      groupId: Number(entry.groupId || 0),
      groupName: entry.groupName || '',
      optionId: Number(entry.optionId || 0),
      optionName: entry.optionName || '',
      priceDelta: Number(entry.priceDelta || 0),
    }));
  } catch (error) {
    return [];
  }
};

const mapOrderItemToUiItem = (orderItem) => {
  const item = orderItem.items || {};
  const rawModifiers = parseModifierSnapshot(orderItem.selected_modifier_options_snapshot);
  const modifiers = normalizeModifierSummary(rawModifiers);

  return {
    id: orderItem.item_id ?? item.id,
    itemId: orderItem.item_id ?? item.id,
    orderItemId: orderItem.id,
    productionAreaId: orderItem.production_area_id ?? null,
    name: orderItem.item_name_snapshot || item.name || '',
    description: item.description || '',
    price: Number(orderItem.item_price_snapshot ?? item.price ?? 0),
    kdv: Number(orderItem.item_kdv_rate_snapshot ?? item.kdv_rate ?? item.kdv ?? 0),
    image: item.image_url || item.image || '',
    quantity: orderItem.quantity,
    notes: orderItem.notes ?? null,
    status: orderItem.status,
    modifiers,
    selectedModifierOptions: buildSelectedModifierPayload(rawModifiers),
  };
};

export const fetchFloors = async (venueId) => {
  try {
    const { data, error } = await supabase
      .from('floors')
      .select('id, name')  // Assuming 'name' is the column that stores the floor name
      .eq('venue_id', venueId)
      .order('name', { ascending: true });  // Sorting by floor name

    if (error) {
      throw error;
    }

    // The map here just extracts the floor names into a simple array
    return data;
  } catch (error) {
    console.error('Error fetching floors', error);
    return [];
  }
};

export const fetchTablesWithFloor = async (venueId, currentFloorId) => {
  try {
    const { data, error } = await supabase
      .from('tables')
      .select('*')
      .eq('venue_id', venueId)
      .eq('floor_id', currentFloorId)
      .order('sort_order', { ascending: true });
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching tables', error);
    return [];
  }
};

export const fetchAllTablesWithVenueId = async (venueId) => {
  try {
    const { data, error } = await supabase
      .from('tables')
      .select('*')
      .eq('venue_id', venueId)
      .order('sort_order', { ascending: true });
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching tables', error);
    return [];
  }
};

export const fetchOrdersByVenue = async (venueId) => {
    try {
      const { data, error } = await supabase
      .from('orders')
      .select(`
          *,
          order_items (
              *,
              items (*)
          ),
          tables (venue_id, name)
      `)
      .eq('tables.venue_id', venueId);
        if (error) throw error;
        const transformedData = data.map(order => {
        const items = order.order_items.map(mapOrderItemToUiItem);

        // Remove the order_items field from the order object
        const { order_items, ...restOfOrder } = order;

        return {
            ...restOfOrder,
            notes: order.notes ?? null,
            items
        }
        });
        return transformedData
    } catch (error) {
        console.error('Error fetching orders', error);
        return [];
    }
}

export const fetchOrdersByTable = async (tableId, venueId) => {
  try {
    const { data, error } = await supabase
      .from('orders')
      .select(`
        *,
        order_items (
          *,
          items (*)
        ),
        tables!inner (venue_id)
      `)
      .eq('tables.venue_id', venueId)
      .eq('table_id', tableId)
      .neq('status', 'deleted');
      
    if (error) throw error;
    const transformedData = data.map((order) => {
      const { order_items, ...restOfOrder } = order;

      return {
        ...restOfOrder,
        notes: order.notes ?? null,
        items: order_items.map(mapOrderItemToUiItem),
      };
    });
    return transformedData;
  } catch (error) {
    console.error('Error fetching orders', error);
    return [];
  }
};

export const fetchActiveMenuWithCategoriesAndItems = async (venueId, getAccessTokenSilently) => {
  try {
    const token = await getAccessTokenSilently();
    const response = await fetch(`${API_URL}/api/v1/venues/${venueId}/menu`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch venue menu (${response.status})`);
    }

    const menu = await response.json();
    const categories = (menu.categories || [])
      .filter((category) => category?.isSystemCategory !== true)
      .map((category) => ({
        ...category,
        items: (category.items || [])
          .filter((item) => item?.status === 'active')
          .map((item) => ({
            id: item.id,
            name: item.name,
            description: item.description,
            price: Number(item.price || 0),
            kdv: Number(item.kdvRate || 0),
            image: item.imageUrl || '',
            status: item.status,
            isAvailable: item.isAvailable ?? true,
            modifierGroups: item.modifierGroups || [],
          })),
      }))
      .filter((category) => category.items.length > 0);

    return {
      menuId: menu.id,
      categories,
    };
  } catch (error) {
    console.error('Error fetching venue menu:', error);
    return null;
  }
};

export const fetchOrderById = async (orderId) => {
  try {
    const { data, error } = await supabase.from('orders').select(`
      *,
      order_items (
        *,
        items (*)
      ),
      tables!inner (venue_id, name)
    `).eq('id', orderId).single();
    if (error) throw error;

    const transformedOrder = {
      ...data,
      notes: data.notes ?? null,
      items: data.order_items.map(mapOrderItemToUiItem)
    };
    return transformedOrder;
  } catch (error) {
    console.error('Error fetching order by ID', error);
    return null;
  }
};
